import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Callout, ConditionalWrapper, Tooltip, useOverlayContext, Page, Typography, Card, Grid, GridItem, Button, ConfirmationModal } from 'seagull';
import ContactSupportButton from '~/components/ContactSupportButton';
import { openInNewTabProps, routes } from '~/constants';
import { useEventReporter } from '~/event-reporter';
import { useIntegration } from '~/hooks/useIntegration';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { integrationModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { isAccountingQbo } from '~/types/api.model';
import { AccountingAdvancedSettings } from '../AccountingAdvancedSettings/AccountingAdvancedSettings';
import { MapAccountsModal } from '../MapAccountsModal/MapAccountsModal';
import { MapContactsModal } from '../MapContactsModal/MapContactsModal';
import { MapServicesModal } from '../MapServicesModal/MapServicesModal';
import styles from './AccountingSetupPage.module.scss';
export function AccountingSetupPage(param) {
    let { accounting } = param;
    var _ref;
    const { t: texts } = useLocaleKeys('integrations.accounting');
    const { t: commonTexts } = useLocaleKeys('common');
    const { showModal } = useOverlayContext();
    const isPendingAccountingIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(accounting));
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const { disconnect: disconnectFromAccounting } = useIntegration(accounting);
    const history = useHistory();
    const accountingIntegration = useStore((state)=>selectors.integration.accountingIntegration(state, accounting));
    const [isLoading, setIsLoading] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const isLinked = accountingIntegration && integrationModel.isLinked(accountingIntegration);
    const { reportToAppcues, reportToGtm, reportToHeap, reportToHubspot } = useEventReporter();
    const { logBI, fetchIntegrations } = useStoreActions();
    const { name: accountingName, biCodes, fullName: accountingFullName, helpArticle, testIdPrefix } = getAccountingDetails(accounting);
    const title = texts.setupTitle({
        accountingFullName
    });
    const subheader = texts.manageConnection({
        companyName: (_ref = accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.companyName()) !== null && _ref !== void 0 ? _ref : ''
    });
    useEffect(()=>{
        setIsLoading(true);
        fetchIntegrations().then((integrations)=>{
            const accountingInteg = integrationModel.accountingIntegration(integrations, accounting);
            const hasIntegration = Boolean(accountingInteg);
            const isIntegrationLinkedAndNotReady = accountingInteg && accountingInteg.isLinked() && !accountingInteg.isReady();
            if (!hasIntegration || isIntegrationLinkedAndNotReady) history.push(routes.availableApps);
        }).finally(()=>setIsLoading(false));
    }, []);
    const subHeader = useMemo(()=>getSubHeaderByIntegration(accountingIntegration !== null && accountingIntegration !== void 0 ? accountingIntegration : undefined), [
        accountingIntegration
    ]);
    const disconnect = async ()=>{
        const userApprovedDisconnect = await showModal({
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    testId: "".concat(testIdPrefix, "-disconnect-modal"),
                    title: texts.disconnectFromTitle({
                        accountingFullName
                    }),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.confirmDisconnect({
                            accountingFullName
                        })
                    }),
                    onCloseButtonClick: ()=>dismissModal(false),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    primaryButtonLabel: commonTexts.buttons.disconnect(),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    skin: "danger"
                });
            }
        });
        if (userApprovedDisconnect) {
            setIsDisconnecting(true);
            logBI(biCodes.disconnect, {
                vendorId,
                userId
            });
            if (isAccountingQbo(accounting)) {
                reportToAppcues('button-click', 'qbo-vendor-disconnect-connection');
                reportToGtm('button-click', 'qbo-vendor-disconnect-connection', {
                    vendorId,
                    userId
                });
                reportToHubspot('vendor_disconnects_qbo_connection');
            }
            await disconnectFromAccounting();
            setIsDisconnecting(false);
            await fetchIntegrations();
            history.push(routes.availableApps);
        }
    };
    const chooseAccounts = async ()=>{
        logBI(biCodes.chooseAccounts, {
            vendorId,
            userId
        });
        if (isAccountingQbo(accounting)) {
            reportToHeap('integrations', 'click', 'qbo-choose-accounts');
            reportToHubspot('vendor_started_contact_sync');
        }
        await showModal({
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapAccountsModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    const mapContacts = async ()=>{
        logBI(biCodes.mapContacts, {
            vendorId,
            userId
        });
        if (isAccountingQbo(accounting)) {
            reportToAppcues('button-click', 'qbo-vendor-started-contacts-map');
            reportToGtm('button-click', 'qbo-vendor-started-contacts-map', {
                vendorId,
                userId
            });
            reportToHeap('integrations', 'click', 'qbo-map-contacts');
            reportToHubspot('vendor_started_contact_sync');
        }
        await showModal({
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapContactsModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    const mapServices = async ()=>{
        logBI(biCodes.mapServices, {
            vendorId,
            userId
        });
        if (isAccountingQbo(accounting)) {
            reportToAppcues('button-click', 'qbo-vendor-started-services-map');
            reportToGtm('button-click', 'qbo-vendor-started-services-map', {
                vendorId,
                userId
            });
            reportToHeap('integrations', 'click', 'qbo-map-services');
            reportToHubspot('vendor_started_services_sync');
        }
        await showModal({
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapServicesModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: "".concat(testIdPrefix, "-setup-page"),
        header: /*#__PURE__*/ _jsx("div", {
            className: styles.pageHeader,
            children: title
        }),
        subheader: /*#__PURE__*/ _jsxs("div", {
            className: styles.pageSubHeader,
            children: [
                subheader,
                /*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({
                    as: "a",
                    href: helpArticle.setup
                }, openInNewTabProps), {
                    children: "Learn more"
                }))
            ]
        }),
        loading: isLoading,
        onClickBack: ()=>{
            if (history.length > 1) {
                history.goBack();
            } else {
                history.push(routes.availableApps);
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.pageContainer,
            children: [
                (accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isMultipleAccountsConnected()) && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    testId: "multiple-accounts-callout",
                    withAction: /*#__PURE__*/ _jsx(Button, {
                        testId: "multiple-accounts-callout-button",
                        skin: "primaryOutline",
                        onClick: ()=>history.push(routes.businessDetails),
                        children: "Go to Billing Settings"
                    }),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        testId: "multiple-accounts-callout-text",
                        children: texts.multipleAccountsCallout({
                            accountingFullName
                        })
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    testId: "connection-settings",
                    header: texts.connectionSettingsHeader(),
                    subHeader: subHeader,
                    isError: !accountingIntegration || accountingIntegration.isErrored(),
                    children: [
                        /*#__PURE__*/ _jsx(GridItemLeft, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: texts.selectAccountsUsed({
                                    accountingName
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(GridItemRight, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                className: styles.button,
                                testId: "choose-accounts",
                                intent: Intent.AccountingMapAccounts,
                                onClick: chooseAccounts,
                                disabled: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isErrored(),
                                children: "Choose accounts"
                            })
                        }),
                        isLinked && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(GridItemLeft, {
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: texts.mapContactsTo({
                                            accountingName
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemRight, {
                                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                        condition: isPendingAccountingIntegrationSetup,
                                        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                                title: /*#__PURE__*/ _jsx("div", {
                                                    "data-testid": "missing-account-mapping-error-contacts",
                                                    children: texts.toMapContactsMapAccountsFirst()
                                                }),
                                                children: children
                                            }),
                                        children: /*#__PURE__*/ _jsx("span", {
                                            "data-testid": "map-contacts-button",
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                className: styles.button,
                                                disabled: isPendingAccountingIntegrationSetup,
                                                testId: "map-contacts",
                                                intent: Intent.AccountingSetupMapContacts,
                                                onClick: mapContacts,
                                                children: "Map contacts"
                                            })
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemLeft, {
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: texts.mapServicesTo({
                                            accountingName
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemRight, {
                                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                        condition: isPendingAccountingIntegrationSetup,
                                        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                                title: /*#__PURE__*/ _jsx("div", {
                                                    "data-testid": "missing-account-mapping-error-services",
                                                    children: texts.toMapServicesMapAccountsFirst()
                                                }),
                                                children: children
                                            }),
                                        children: /*#__PURE__*/ _jsx("span", {
                                            "data-testid": "map-services-button",
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                className: styles.button,
                                                disabled: isPendingAccountingIntegrationSetup,
                                                testId: "map-services",
                                                intent: Intent.AccountingMapServices,
                                                onClick: mapServices,
                                                children: "Map services"
                                            })
                                        })
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Section, {
                    testId: "advanced-settings",
                    header: texts.advancedSettings(),
                    children: /*#__PURE__*/ _jsx(AccountingAdvancedSettings, {
                        accounting: accounting,
                        isLinked: isLinked || false
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    testId: "disconnect-".concat(testIdPrefix),
                    children: [
                        /*#__PURE__*/ _jsx(GridItemLeft, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                skin: "error",
                                children: texts.disconnectFrom({
                                    accountingFullName
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(GridItemRight, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                className: styles.button,
                                skin: "danger",
                                testId: "disconnect",
                                intent: Intent.AccountingDisconnect,
                                onClick: disconnect,
                                loading: isDisconnecting,
                                disabled: !!accountingIntegration && accountingIntegration.isErrored(),
                                children: "Disconnect"
                            })
                        })
                    ]
                })
            ]
        })
    });
}
const getSubHeaderByIntegration = (integration)=>{
    if (integration && integration.isErrored()) return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "Failed to connect. Please ",
            /*#__PURE__*/ _jsx(ContactSupportButton, {
                skin: "error"
            }),
            "."
        ]
    });
    return '';
};
const Section = (param)=>{
    let { testId, header, subHeader, isError, children } = param;
    return /*#__PURE__*/ _jsxs(Card, {
        testId: "".concat(testId, "-section"),
        className: styles.section,
        children: [
            header && /*#__PURE__*/ _jsxs("div", {
                className: styles.header,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: "".concat(testId, "-header"),
                        weight: "normal",
                        variant: "h5",
                        children: header
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: isError ? 'error' : 'secondary',
                        variant: "paragraph2",
                        testId: "".concat(testId, "-sub-header"),
                        children: subHeader
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Grid, {
                align: "center",
                rowGap: "small",
                children: children
            })
        ]
    });
};
const GridItemRight = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 2,
        justify: "center",
        children: children
    });
};
const GridItemLeft = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 10,
        children: children
    });
};
